import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ArrowRight, Sparkles } from "lucide-react";

const HERO_IMAGE = "/images/marilyn.jpeg";
const WHEEL_IMAGE = "https://images.unsplash.com/photo-1595351298020-038700609878?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHx3b21hbiUyMHBvdHRlcnklMjB3aGVlbHxlbnwwfHx8fDE3OTM4NDQ3ODl8MA&ixlib=rb-4.1.0&q=85";
const CANDLE = "https://images.unsplash.com/photo-1622691078858-58f9eb8825e0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHxjYW5kbGVsaWdodCUyMHBvdHRlcnl8ZW58MHx8fHwxNzkzODQ0Nzg5fDA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const [courses, setCourses] = useState([]);
  const [ask, setAsk] = useState("");
  const [answer, setAnswer] = useState("");
  const [asking, setAsking] = useState(false);

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
  }, []);

  const quickAsk = async (e) => {
    e.preventDefault();
    if (!ask.trim()) return;
    setAsking(true);
    setAnswer("");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: ask }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const chunk of lines) {
          if (chunk.startsWith("data: ")) {
            const t = chunk.slice(6).replace(/\\n/g, "\n");
            setAnswer((a) => a + t);
          }
        }
      }
    } catch (err) {
      setAnswer("Marilyn is away from the wheel for a moment. Please try again.");
    }
    setAsking(false);
  };

  return (
    <main data-testid="home-page" className="bg-[#0D0C0A] text-[#F5F2EB]">
      {/* HERO */}
      <section className="relative min-h-screen w-full overflow-hidden">
        <div className="absolute inset-0 z-0 bg-[radial-gradient(ellipse_at_75%_40%,rgba(212,175,55,0.12),transparent_60%)]" />

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 pt-32 pb-20 min-h-screen grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 lg:order-2 relative animate-fade-up">
            <div className="absolute -inset-3 border border-[#D4AF37]/30 rounded-sm translate-x-4 translate-y-4" />
            <div className="relative overflow-hidden rounded-sm shadow-[0_30px_80px_rgba(0,0,0,0.6)]">
              <img
                src={HERO_IMAGE}
                alt="Marilyn Dale at the pottery wheel in an evening gown"
                data-testid="hero-marilyn-photo"
                className="w-full h-auto max-h-[78vh] object-cover object-top"
              />
            </div>
          </div>

          <div className="lg:col-span-7 lg:order-1 max-w-2xl animate-fade-up">
            <p className="label-eyebrow mb-6 flex items-center gap-3">
              <span className="h-px w-10 bg-[#D4AF37]" />
              Marilyn Dale · Silk and Slip, Making Pottery
            </p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight mb-8">
              Where you just might <br />
              find <em className="text-[#D4AF37] font-normal">wheel love</em>.
            </h1>
            <p className="text-lg sm:text-xl text-[#D5D0C5] font-light leading-relaxed max-w-xl mb-10">
              Learn pottery from a potter who throws in an evening gown — because craft and elegance belong together. Live lessons, structured classes, and a tutor who always answers.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/classes" data-testid="hero-classes-btn" className="btn-gold px-8 py-4 rounded-full inline-flex items-center gap-2 text-sm">
                Browse the Classes <ArrowRight size={16} />
              </Link>
              <Link to="/chat" data-testid="hero-chat-btn" className="btn-outline-gold px-8 py-4 rounded-full inline-flex items-center gap-2 text-sm">
                <Sparkles size={16} /> Ask Marilyn anything
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* QUICK ASK */}
      <section className="relative py-28 px-6 md:px-12 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src={CANDLE} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0D0C0A]/80" />
        </div>
        <div className="relative max-w-4xl mx-auto">
          <p className="label-eyebrow mb-6 text-center">A moment at the wheel</p>
          <h2 className="font-display text-4xl sm:text-5xl text-center leading-tight mb-10">
            Have a question? <span className="italic text-[#D4AF37]">Ask away.</span>
          </h2>
          <form onSubmit={quickAsk} className="glass rounded-2xl p-2 flex items-center gap-2" data-testid="quick-ask-form">
            <input
              data-testid="quick-ask-input"
              type="text"
              value={ask}
              onChange={(e) => setAsk(e.target.value)}
              placeholder="e.g. Why does my clay keep collapsing when I pull up the walls?"
              className="flex-1 bg-transparent border-0 focus:outline-none px-6 py-4 text-[#F5F2EB] placeholder:text-[#B3AFA8]/70"
            />
            <button type="submit" disabled={asking} data-testid="quick-ask-submit" className="btn-gold px-6 py-3 rounded-xl disabled:opacity-60">
              {asking ? "Thinking…" : "Ask Marilyn"}
            </button>
          </form>
          {answer && (
            <div data-testid="quick-ask-answer" className="mt-10 glass rounded-2xl p-8 lesson-prose">
              <p className="label-eyebrow mb-4">Marilyn says</p>
              {answer.split("\n").filter(Boolean).map((line, i) => <p key={i}>{line}</p>)}
            </div>
          )}
        </div>
      </section>

      {/* COURSES PREVIEW */}
      <section id="classes" className="py-32 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-12 gap-10 mb-16 items-end">
            <div className="md:col-span-7">
              <p className="label-eyebrow mb-5">The Classes</p>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight">
                From your first <span className="italic text-[#D4AF37]">centering</span> to a beautifully decorated surface.
              </h2>
            </div>
            <div className="md:col-span-5 md:text-right">
              <p className="text-[#D5D0C5] leading-relaxed max-w-md md:ml-auto">
                Three courses, carefully paced. Follow along at your own pace — Marilyn is always one message away for questions.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-12 gap-6">
            {courses.map((c, idx) => (
              <Link
                key={c.id}
                to={`/classes/${c.id}`}
                data-testid={`course-card-${c.id}`}
                className={`group relative overflow-hidden border border-[#332D29] bg-[#1A1715] rounded-sm hover:border-[#D4AF37]/50 transition-all duration-500 ${
                  idx === 0 ? "md:col-span-5 md:row-span-2" : "md:col-span-7"
                }`}
                style={{ minHeight: idx === 0 ? 560 : 280 }}
              >
                <img src={c.cover} alt={c.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 opacity-60 group-hover:opacity-70" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D0C0A] via-[#0D0C0A]/40 to-transparent" />
                <div className="relative z-10 p-8 h-full flex flex-col justify-end">
                  <p className="label-eyebrow mb-4">{c.level} · {c.duration}</p>
                  <h3 className="font-display text-3xl lg:text-4xl mb-3 group-hover:text-[#D4AF37] transition-colors">{c.title}</h3>
                  <p className="text-[#D5D0C5] text-sm mb-5 max-w-md">{c.tagline}</p>
                  <span className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase inline-flex items-center gap-2">
                    Enter the studio <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT STRIP */}
      <section className="py-32 px-6 md:px-12 border-t border-[#332D29]/50">
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-12 gap-16 items-center">
          <div className="md:col-span-5 relative">
            <div className="aspect-[3/4] overflow-hidden">
              <img src={WHEEL_IMAGE} alt="Pottery wheel" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -right-6 glass rounded-sm p-5 hidden md:block">
              <p className="font-display italic text-2xl text-[#D4AF37]">"Clay listens."</p>
              <p className="text-xs text-[#B3AFA8] mt-2 tracking-wider">— Marilyn</p>
            </div>
          </div>
          <div className="md:col-span-7">
            <p className="label-eyebrow mb-5">The teacher</p>
            <h2 className="font-display text-4xl sm:text-5xl leading-tight mb-8">
              Pottery, taught the way it deserves to be — with <span className="italic text-[#D4AF37]">reverence</span> and a little glamour.
            </h2>
            <div data-testid="about-story" className="space-y-5 mb-10 text-[#D5D0C5] leading-relaxed">
              <p>
                I've been teaching Pottery for almost 50 years! Darling, here's a real truth… I was thrown out of my very first Pottery Class! Through the kindness of a neighbor I was re-introduced to the real… And fell madly in love.
              </p>
              <p>
                There's just something about Pottery… The clay between your hands, your foot at that wheel making it go faster and slower, the elevation of your spirit into a different place… A place of imagination, manifestation, bliss! The disappointment gets swallowed up and forgotten as each success builds on the next. It's a spiritual journey with a medium called clay.
              </p>
            </div>
            <Link to="/signup" data-testid="about-join-btn" className="btn-gold px-8 py-4 rounded-full inline-flex items-center gap-2 text-sm">
              Join the Studio <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <footer className="py-16 px-6 md:px-12 border-t border-[#332D29]/50">
        <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div>
            <p className="font-display text-xl text-[#F5F2EB]">Marilyn Dale, <span className="italic text-[#D4AF37]">Silk and Slip</span></p>
            <p className="text-xs tracking-[0.3em] uppercase text-[#B3AFA8] mt-2">Where you just might find wheel love</p>
          </div>
          <p className="text-xs text-[#B3AFA8]">© 2026 Marilyn Dale, Silk and Slip, Making Pottery</p>
        </div>
      </footer>
    </main>
  );
}
